import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import type { UserInfo } from '../types/UserInfoTypes';
import type { GetStartedDto } from '../components/pages/get-started/getStartedTypes';
import type { AccountOnboardingStep } from '@server/account-onboarding';
import type { QuestionnaireDto } from '../components/pages/questionnaire/questionnaireTypes';
import type { Permission } from '@server/permissions';
import type { UserPreferencesTableColumnSettings } from '@server/user-preferences';

export interface UserInfoState {
  data?: UserInfo;
  isLoading: boolean;
  error?: string;
  isNoAccountSetError?: boolean;
}

const initialState: UserInfoState = {
  data: undefined,
  isLoading: true,
  isNoAccountSetError: false,
};

export const userInfoSlice = createSlice({
  name: 'userInfo',
  initialState,
  reducers: {
    setUserFetchSuccess: (state, action: PayloadAction<UserInfo>) => {
      state.isLoading = false;
      state.data = action.payload;
      if (state.data && state.data.account) {
        state.data.account.name = !!state.data.account?.name ? state.data.account.name : 'Untitled';
      }
    },
    setUserPermissions: (
      state,
      action: PayloadAction<{
        orgPermissions: Record<string, Permission[]>;
        accountPermissions: Permission[];
      }>,
    ) => {
      state.data = {
        ...state.data,
        orgPermissions: action.payload.orgPermissions,
        accountPermissions: action.payload.accountPermissions,
      };
    },
    setUserFetchError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    setNoAccountSetError: (state) => {
      state.isNoAccountSetError = true;
    },
    setUserInfo: (state, action: PayloadAction<Partial<UserInfo>>) => {
      state.data = { ...state.data, ...action.payload };
    },
    setUserGetStarted: (state, action: PayloadAction<GetStartedDto>) => {
      state.data = {
        ...state.data,
        preferences: { ...state.data?.preferences, getStarted: action.payload },
      };
    },
    updateOnboardingStep: (state, action: PayloadAction<AccountOnboardingStep>) => {
      if (!state.data || !state.data.account) {
        return;
      }
      state.data.account.onboardingStep = action.payload;
    },
    setTrialEndDate: (state, action: PayloadAction<{ trialEndDate: string }>) => {
      if (!state.data || !state.data.account) {
        return;
      }
      const { trialEndDate } = action.payload;
      state.data.account.trialEndDate = trialEndDate;
    },
    setUserQuestionnaire: (state, action: PayloadAction<QuestionnaireDto>) => {
      state.data = {
        ...state.data,
        preferences: { ...state.data?.preferences, questionnaire: action.payload },
      };
    },
    setUserTableColumnsSettings: (
      state,
      action: PayloadAction<UserPreferencesTableColumnSettings[]>,
    ) => {
      state.data = {
        ...state.data,
        preferences: { ...state.data?.preferences, tableColumnSettings: action.payload },
      };
    },
    setUserMcpOrgId: (state, action: PayloadAction<string>) => {
      state.data = { ...state.data, mcpOrgId: action.payload };
    },
  },
});

export const {
  setUserInfo,
  setUserFetchSuccess,
  setUserFetchError,
  setUserGetStarted,
  updateOnboardingStep,
  setTrialEndDate,
  setUserQuestionnaire,
  setUserPermissions,
  setUserTableColumnsSettings,
  setUserMcpOrgId,
  setNoAccountSetError,
} = userInfoSlice.actions;

export const selectUserInfoData = (state: RootState) => state.userInfo.data;

export const selectUserMcpOrgId = (state: RootState) => state.userInfo.data?.mcpOrgId;

export const selectIsNoAccountSetError = (state: RootState) => state.userInfo.isNoAccountSetError;

export const selectUserGetStarted = (state: RootState) =>
  state.userInfo.data?.preferences?.getStarted;

export const selectUserQuestionnaire = (state: RootState) =>
  state.userInfo.data?.preferences?.questionnaire;

export const selectUserRole = (state: RootState) =>
  state.userInfo.data?.preferences?.questionnaire?.data?.role;

export const selectUserTableColumnSettings = (state: RootState) =>
  state.userInfo.data?.preferences?.tableColumnSettings;

export const selectUserInterests = (state: RootState) =>
  state.userInfo.data?.preferences?.questionnaire?.data?.interests;

export const selectDefaultCreationCrmOrgId = (state: RootState) =>
  state.userInfo.data?.preferences?.defaultCreationCrmOrgId;

export const selectOrgPermissions = (state: RootState) => state.userInfo.data?.orgPermissions;

export const selectAccountPermissions = (state: RootState) =>
  state.userInfo.data?.accountPermissions;

export const selectUserAccountId = (state: RootState) => state.userInfo.data?.account?.id;

export const selectPlanFeatures = (state: RootState) => state.userInfo.data?.account?.planFeatures;

export const selectIsDisableAi = (state: RootState) => state.userInfo.data?.account?.disableAi;

export const selectIsBuildModeEnabled = (state: RootState) =>
  state.userInfo.data?.account?.isBuildModeEnabled ?? false;

export default userInfoSlice.reducer;
